import { pythonClient } from './axios.client.js';
import { getLangGraphBackendUrl } from '../utils/langgraphConfig.js';
// Lazy import connectionTimeoutService to avoid circular dependency
let connectionTimeoutService = null;

const getConnectionTimeoutService = async () => {
  if (!connectionTimeoutService) {
    try {
      connectionTimeoutService = (await import('../services/connectionTimeoutService.js')).default;
    } catch (error) {
      console.warn('Failed to load connectionTimeoutService:', error);
    }
  }
  return connectionTimeoutService;
};

// Private methods that use raw pythonClient (no timeout handling)
const makeRawRequest = async (method, url, config = {}) => {
  // Ensure the URL has the correct /api/v1 prefix for MCP endpoints
  let fullUrl;
  if (url.startsWith('http')) {
    fullUrl = url;
  } else {
    // Add /api/v1 prefix if not present
    const apiPath = url.startsWith('/api/v1') ? url : `/api/v1${url.startsWith('/') ? '' : '/'}${url}`;
    fullUrl = `${getLangGraphBackendUrl()}${apiPath}`;
  }

  const requestConfig = {
    ...config,
    method,
    url: fullUrl,
  };

  if (config.body) {
    requestConfig.data = config.body;
  }

  return await pythonClient(requestConfig);
};

/**
 * Enhanced MCP (Model Context Protocol) API Service
 * Handles communication with MCP server endpoints using HTTP and WebSocket transports
 */

/**
 * Enhanced MCP WebSocket Client with Real-time Event Streaming
 */
class MCPWebSocketClient {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.connectionState = 'disconnected'; // disconnected, connecting, connected, reconnecting
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.backoffMultiplier = 1.5;

    // Enhanced tracking
    this.messageHandlers = new Map();
    this.requestHandlers = new Map();
    this.eventListeners = new Map();
    this.pendingRequests = new Map();
    this.subscriptions = new Map(); // Active subscriptions
    this.eventStream = new Map(); // Real-time event streams
    this.contextSync = new Map(); // Context synchronization

    // Connection management
    this.heartbeatInterval = null;
    this.heartbeatTimeout = null;
    this.lastActivity = null;
    this.connectionId = null;
    this.connectionMetadata = {};
    this.reconnectTimer = null;

    // Enhanced health monitoring
    this.connectionHealth = {
      lastPing: null,
      lastPong: null,
      pingLatency: null,
      messageCount: 0,
      errorCount: 0,
      reconnectCount: 0,
      lastActivity: null,
      uptime: 0,
      throughput: 0,
    };

    // Performance metrics
    this.performance = {
      messagesPerSecond: 0,
      bytesTransferred: 0,
      averageLatency: 0,
      peakThroughput: 0,
    };

    // Context synchronization
    this.contextState = {
      lastSync: null,
      pendingSyncs: new Set(),
      syncInProgress: false,
      version: 0,
    };

    // Initialize enhanced features
    this._initializeEventStreaming();
  }

  /**
   * Initialize event streaming capabilities
   */
  _initializeEventStreaming() {
    // Event stream buffer for offline/online scenarios
    this.eventBuffer = [];
    this.maxEventBufferSize = 1000;

    // Context change tracking
    this.contextChanges = new Map();
    this.contextVersion = 0;

    // Real-time subscriptions
    this.activeSubscriptions = new Set();

    // Performance monitoring
    this.messageTimestamps = [];
    this.performanceInterval = setInterval(() => {
      this._updatePerformanceMetrics();
    }, 1000);
  }

  /**
   * Update performance metrics
   */
  _updatePerformanceMetrics() {
    const now = Date.now();
    const recentMessages = this.messageTimestamps.filter(
      (timestamp) => now - timestamp < 1000,
    );

    this.performance.messagesPerSecond = recentMessages.length;

    // Calculate average latency
    if (this.connectionHealth.lastPong && this.connectionHealth.lastPing) {
      this.performance.averageLatency = this.connectionHealth.pingLatency;
    }

    // Update throughput
    this.performance.peakThroughput = Math.max(
      this.performance.peakThroughput,
      this.performance.messagesPerSecond,
    );

    // Clean old timestamps
    this.messageTimestamps = this.messageTimestamps.slice(-100);
  }

  connect() {
    return new Promise((resolve, reject) => {
      // Prevent multiple concurrent connection attempts
      if (this.connectionState === 'connecting' || this.connectionState === 'connected') {
        if (this.connected) {
          resolve();
          return;
        }
        // Wait for existing connection attempt
        this.once('connected', resolve);
        this.once('connection_failed', reject);
        return;
      }

      this.connectionState = 'connecting';
      this._emit('connecting');

      const backendUrl = getLangGraphBackendUrl();
      const wsUrl = backendUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      const fullUrl = `${wsUrl}/api/v1/mcp/ws`;

      console.log(`🔗 Attempting MCP WebSocket connection to: ${fullUrl}`);

      try {
        // Clear any existing connection
        this._cleanup();

        // Generate connection ID for state tracking
        this.connectionId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.connectionMetadata = {
          connectionId: this.connectionId,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
          attempt: this.reconnectAttempts + 1,
        };

        this.ws = new WebSocket(fullUrl);

        // Connection timeout
        const connectionTimeout = setTimeout(() => {
          if (this.connectionState === 'connecting') {
            console.error('🔗 WebSocket connection timeout');
            this._cleanup();
            this._emit('connection_failed', new Error('Connection timeout'));
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);

        this.ws.onopen = () => {
          console.log('✅ MCP WebSocket connected successfully');
          clearTimeout(connectionTimeout);
          this.connected = true;
          this.connectionState = 'connected';
          this.reconnectAttempts = 0;
          this.lastActivity = Date.now();

          // Send MCP initialize request as per protocol
          console.log('🔗 Sending MCP initialize request...');
          this.ws.send(JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'initialize',
            params: {
              protocolVersion: '2024-11-05',
              capabilities: {
                tools: {},
                resources: {},
                prompts: {},
                logging: {},
              },
              clientInfo: {
                name: 'chatbot-frontend',
                version: '1.0.0',
              },
            },
          }));

          this._startHeartbeat();
          this._emit('connected', {
            connectionId: this.connectionId,
            url: fullUrl,
          });
          resolve();
        };

        this.ws.onmessage = (event) => {
          this._handleMessage(event.data);
          this.lastActivity = Date.now();
          this.connectionHealth.messageCount++;
        };

        this.ws.onclose = (event) => {
          console.log('🔌 MCP WebSocket closed:', event.code, event.reason, 'wasClean:', event.wasClean);
          console.log('🔌 Previous connection state:', this.connectionState, 'was connected:', this.connected);
          console.log('Close event details:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            target: event.target?.url || fullUrl,
          });

          clearTimeout(connectionTimeout);
          const wasConnected = this.connected;
          this.connected = false;
          this.connectionState = 'disconnected';
          this._stopHeartbeat();
          this._cleanup();

          if (wasConnected) {
            this._emit('disconnected', {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
              connectionId: this.connectionId,
            });
          }

          // Auto-reconnect logic
          const shouldReconnect = (
            event.code !== 1000 // Not normal closure
            && event.code !== 1001 // Not going away
            && event.code !== 1008 // Policy violation (rate limit)
            && this.reconnectAttempts < this.maxReconnectAttempts
          );

          if (shouldReconnect) {
            this.connectionHealth.reconnectCount++;
            console.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts}`);
            this._scheduleReconnect();
          } else {
            const errorMsg = shouldReconnect
              ? 'Max reconnection attempts reached'
              : `WebSocket closed with code ${event.code}: ${event.reason}`;
            console.log(`❌ ${errorMsg}`);
            this._emit('connection_failed', new Error(errorMsg));
            reject(new Error(errorMsg));
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ MCP WebSocket error:', error);
          console.error('WebSocket details:', {
            readyState: this.ws?.readyState,
            url: fullUrl,
            connectionId: this.connectionId,
          });

          this.connectionHealth.errorCount++;
          this._emit('error', error);
          // Don't reject immediately - let onclose handle it
        };
      } catch (error) {
        console.error('❌ Failed to create WebSocket connection:', error);
        this.connectionState = 'disconnected';
        reject(error);
      }
    });
  }

  disconnect() {
    console.log('🔌 Disconnecting MCP WebSocket');
    this.connectionState = 'disconnected';
    this._cleanup();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }

    this.connected = false;
    this._stopHeartbeat();
    this.pendingRequests.clear();

    this._emit('disconnected', {
      code: 1000,
      reason: 'Client disconnecting',
      wasClean: true,
    });
  }

  // Event handling methods
  on(event, handler) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(handler);
  }

  off(event, handler) {
    if (this.eventListeners.has(event)) {
      const handlers = this.eventListeners.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  once(event, handler) {
    const onceHandler = (...args) => {
      handler(...args);
      this.off(event, onceHandler);
    };
    this.on(event, onceHandler);
  }

  _emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`❌ Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  // Connection state methods
  isConnected() {
    return this.connected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  isConnecting() {
    return this.connectionState === 'connecting' || this.connectionState === 'reconnecting';
  }

  getConnectionState() {
    return {
      state: this.connectionState,
      connected: this.connected,
      connectionId: this.connectionId,
      lastActivity: this.lastActivity,
      reconnectAttempts: this.reconnectAttempts,
      health: { ...this.connectionHealth },
    };
  }

  _startHeartbeat() {
    // Clear any existing heartbeat
    this._stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({
            jsonrpc: '2.0',
            method: 'ping',
            params: { timestamp: Date.now() },
          }));
          console.log('💓 Heartbeat sent');
        } catch (error) {
          console.error('❌ Failed to send heartbeat:', error);
          this._handleConnectionError();
        }
      }
    }, 25000); // 25 seconds (more frequent to prevent timeouts)
  }

  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  _handleConnectionError() {
    console.error('🔌 Connection error detected, attempting to reconnect...');
    this.connected = false;
    this._stopHeartbeat();

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this._scheduleReconnect();
    } else {
      this._emit('error', new Error('Max reconnection attempts reached'));
    }
  }

  _scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * 2 ** (this.reconnectAttempts - 1); // Exponential backoff

    console.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    setTimeout(() => {
      this.connect().catch((error) => {
        console.error('❌ Reconnection failed:', error);
      });
    }, delay);
  }

  _cleanup() {
    // Clear any existing timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    // Clear pending requests
    this.pendingRequests.forEach(({ reject }) => {
      reject(new Error('Connection closed'));
    });
    this.pendingRequests.clear();
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.connectionState = 'reconnecting';
    this._emit('reconnecting', { attempt: this.reconnectAttempts + 1 });

    // Calculate delay with exponential backoff and jitter
    const baseDelay = Math.min(this.reconnectDelay * this.backoffMultiplier ** this.reconnectAttempts, this.maxReconnectDelay);
    const jitter = Math.random() * 0.3 * baseDelay; // 30% jitter
    const delay = baseDelay + jitter;

    console.log(`⏳ Reconnecting in ${delay.toFixed(0)}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect().catch((error) => {
        console.error(`❌ Reconnection attempt ${this.reconnectAttempts} failed:`, error);
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this._scheduleReconnect();
        } else {
          this._emit('max_reconnect_attempts_reached');
        }
      });
    }, delay);
  }

  _startHeartbeat() {
    this._stopHeartbeat();

    // Send heartbeat every 25 seconds
    this.heartbeatInterval = setInterval(() => {
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        const pingTime = Date.now();
        this.connectionHealth.lastPing = pingTime;

        try {
          this.ws.send(JSON.stringify({
            jsonrpc: '2.0',
            method: 'ping',
            params: { timestamp: pingTime },
          }));

          // Set timeout for pong response
          this.heartbeatTimeout = setTimeout(() => {
            if (this.connectionHealth.lastPong !== pingTime) {
              console.warn('⚠️ Heartbeat timeout - no pong received');
              this.connectionHealth.errorCount++;

              // If we've missed several heartbeats, consider connection unhealthy
              if (this.connectionHealth.errorCount > 3) {
                console.error('❌ Multiple heartbeat failures - forcing reconnection');
                this._forceReconnect();
              }
            }
          }, 5000); // 5 second timeout for pong
        } catch (error) {
          console.error('❌ Failed to send heartbeat:', error);
          this.connectionHealth.errorCount++;
        }
      }
    }, 25000);
  }

  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  _forceReconnect() {
    console.log('🔄 Forcing reconnection due to connection issues');
    this._cleanup();
    if (this.ws) {
      this.ws.close(1000, 'Client forcing reconnection');
    }
    this._scheduleReconnect();
  }

  _handleMessage(data) {
    try {
      console.log('📨 Received WebSocket message:', data.substring(0, 200) + (data.length > 200 ? '...' : ''));
      const message = JSON.parse(data);
      console.log('📨 Parsed message:', message);

      // Handle JSON-RPC responses
      if (message.id && this.pendingRequests.has(message.id)) {
        const { resolve, reject } = this.pendingRequests.get(message.id);
        this.pendingRequests.delete(message.id);

        if (message.error) {
          reject(new Error(message.error.message || 'MCP error'));
        } else {
          resolve(message.result);
        }
      }

      // Handle MCP initialize response
      else if (message.id === 1 && message.result) {
        console.log('🔗 MCP initialize response received:', message.result);
        this._emit('initialized', message.result);
        console.log('🔗 MCP protocol initialized successfully');
      }

      // Handle connection established message from server (fallback)
      else if (message.method === 'connection_established' && !message.id) {
        console.log('🔗 MCP WebSocket connection established:', message.params);
        console.log('🔗 Setting WebSocket as connected...');
        this.connected = true;
        this.connectionState = 'connected';
        this._emit('connection_established', message.params);
        console.log('🔗 Emitted connection_established event');
      }

      // Handle ping/pong for heartbeat
      else if (message.method === 'ping' && !message.id) {
        // Respond to ping with pong
        this.ws.send(JSON.stringify({
          jsonrpc: '2.0',
          method: 'pong',
          params: { timestamp: Date.now(), original_timestamp: message.params?.timestamp },
        }));
        console.log('🏓 Pong sent in response to ping');
        return;
      }
      else if (message.method === 'pong') {
        // Handle pong response
        const pongTime = Date.now();
        const originalTimestamp = message.params?.timestamp || message.params?.original_timestamp;

        // Update lastPong to the timestamp we sent in the ping
        this.connectionHealth.lastPong = originalTimestamp;

        // Calculate latency
        if (originalTimestamp) {
          this.connectionHealth.pingLatency = pongTime - originalTimestamp;
        }

        // Clear the heartbeat timeout since we got a pong
        if (this.heartbeatTimeout) {
          clearTimeout(this.heartbeatTimeout);
          this.heartbeatTimeout = null;
        }

        // Reset error count on successful pong
        this.connectionHealth.errorCount = 0;

        console.log(`🏓 Pong received, latency: ${this.connectionHealth.pingLatency}ms, errors reset`);
        return;
      }

      // Handle notifications/events
      else if (message.method && !message.id) {
        this._emit(message.method, message.params || {});
      }
    } catch (error) {
      console.error('❌ Failed to parse WebSocket message:', error);
    }
  }

  async sendRequest(method, params = {}) {
    if (!this.connected) {
      await this.connect();
    }

    return new Promise((resolve, reject) => {
      const id = Date.now() + Math.random();
      const request = {
        jsonrpc: '2.0',
        id,
        method,
        params,
      };

      this.pendingRequests.set(id, { resolve, reject });

      try {
        this.ws.send(JSON.stringify(request));

        // Set timeout for request
        setTimeout(() => {
          if (this.pendingRequests.has(id)) {
            this.pendingRequests.delete(id);
            reject(new Error('Request timeout'));
          }
        }, 10000); // 10 second timeout
      } catch (error) {
        this.pendingRequests.delete(id);
        reject(error);
      }
    });
  }

  on(event, handler) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(handler);
  }

  off(event, handler) {
    if (this.eventListeners.has(event)) {
      const handlers = this.eventListeners.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  _emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`❌ Error in event handler for ${event}:`, error);
        }
      });
    }
  }
}

const MCP_API = {
  // WebSocket client instance
  wsClient: new MCPWebSocketClient(),

  // Server management
  getServerStatus: async () => {
    const response = await pythonClient.get('/mcp/status');
    return response.data;
  },

  startServer: async () => {
    const response = await pythonClient.post('/mcp/server/start');
    return response.data;
  },

  stopServer: async () => {
    const response = await pythonClient.post('/mcp/server/stop');
    return response.data;
  },

  // Flow management
  getFlows: async (params = {}) => {
    const response = await pythonClient.get('/mcp/flows', { params });
    return response.data;
  },

  registerFlowAsTool: async (flowData) => {
    const response = await pythonClient.post('/mcp/register-flow', flowData);
    return response.data;
  },

  getRegisteredFlows: async () => {
    const response = await pythonClient.get('/mcp/flows');
    return response.data;
  },

  // Tool management
  getAvailableTools: async () => {
    const response = await pythonClient.get('/mcp/tools');
    return response.data;
  },

  executeTool: async (toolData) => {
    const response = await pythonClient.post('/mcp/execute', toolData);
    return response.data;
  },

  // Template management
  getTemplates: async (params = {}) => {
    const response = await pythonClient.get('/mcp/templates', { params });
    return response.data;
  },

  createFlowFromTemplate: async (templateData) => {
    const response = await pythonClient.post('/mcp/create-from-template', templateData);
    return response.data;
  },

  // Search and discovery
  searchFlows: async (searchData) => {
    const response = await pythonClient.post('/mcp/search-flows', searchData);
    return response.data;
  },

  listFlows: async (params = {}) => {
    const response = await pythonClient.post('/mcp/list-flows', params);
    return response.data;
  },

  // Flow execution status
  getFlowStatus: async (executionId) => {
    const response = await pythonClient.post('/mcp/get-flow-status', { execution_id: executionId });
    return response.data;
  },

  cancelFlowExecution: async (executionId) => {
    const response = await pythonClient.post('/mcp/cancel-flow-execution', { execution_id: executionId });
    return response.data;
  },

  // Statistics
  getFlowStatistics: async (params = {}) => {
    const response = await pythonClient.post('/mcp/get-flow-statistics', params);
    return response.data;
  },

  // Validation
  validateFlow: async (flowDefinition) => {
    const response = await pythonClient.post('/mcp/validate-flow', { flow_definition: flowDefinition });
    return response.data;
  },

  // Enhanced WebSocket functionality
  connectWebSocket: async () => {
    await MCP_API.wsClient.connect();
    return MCP_API.wsClient;
  },

  disconnectWebSocket: () => {
    MCP_API.wsClient.disconnect();
  },

  // Real-time event subscription
  subscribeToEvents: (eventType, handler) => {
    MCP_API.wsClient.on(eventType, handler);
  },

  unsubscribeFromEvents: (eventType, handler) => {
    MCP_API.wsClient.off(eventType, handler);
  },

  // Connection status and management
  isWebSocketConnected: () => MCP_API.wsClient.isConnected(),

  isWebSocketConnecting: () => MCP_API.wsClient.isConnecting(),

  getWebSocketConnectionState: () => MCP_API.wsClient.getConnectionState(),

  // Enhanced Real-time Event Streaming Methods

  /**
   * Subscribe to real-time events
   * @param {string} eventType - Type of event to subscribe to
   * @param {object} options - Subscription options
   */
  subscribeToEventStream: async (eventType, options = {}) => {
    if (!MCP_API.wsClient.isConnected()) {
      await MCP_API.wsClient.connect();
    }

    const subscriptionId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const subscription = {
      id: subscriptionId,
      eventType,
      options,
      createdAt: new Date().toISOString(),
      active: true,
    };

    // Add to active subscriptions
    MCP_API.wsClient.activeSubscriptions.add(subscriptionId);

    // Send subscription request to server
    try {
      await MCP_API.wsClient.sendRequest('subscribe', {
        id: subscriptionId,
        event_type: eventType,
        options,
      });

      // Store subscription locally
      if (!MCP_API.wsClient.subscriptions.has(eventType)) {
        MCP_API.wsClient.subscriptions.set(eventType, new Set());
      }
      MCP_API.wsClient.subscriptions.get(eventType).add(subscriptionId);

      console.log(`📡 Subscribed to event stream: ${eventType}`);
      return subscriptionId;
    } catch (error) {
      console.error('Failed to subscribe to event stream:', error);
      MCP_API.wsClient.activeSubscriptions.delete(subscriptionId);
      throw error;
    }
  },

  /**
   * Unsubscribe from event stream
   * @param {string} subscriptionId - Subscription ID
   */
  unsubscribeFromEventStream: async (subscriptionId) => {
    if (!MCP_API.wsClient.isConnected()) {
      console.warn('Cannot unsubscribe: not connected');
      return false;
    }

    try {
      await MCP_API.wsClient.sendRequest('unsubscribe', {
        id: subscriptionId,
      });

      // Remove from active subscriptions
      MCP_API.wsClient.activeSubscriptions.delete(subscriptionId);

      // Remove from local tracking
      MCP_API.wsClient.subscriptions.forEach((subs, eventType) => {
        subs.delete(subscriptionId);
        if (subs.size === 0) {
          MCP_API.wsClient.subscriptions.delete(eventType);
        }
      });

      console.log(`🔌 Unsubscribed from event stream: ${subscriptionId}`);
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from event stream:', error);
      throw error;
    }
  },

  /**
   * Get active subscriptions
   */
  getActiveSubscriptions: () => Array.from(MCP_API.wsClient.activeSubscriptions),

  /**
   * Stream context changes
   * @param {string} scope - Context scope to monitor
   * @param {function} onChange - Change handler
   */
  streamContextChanges: async (scope, onChange) => {
    const subscriptionId = await MCP_API.subscribeToEventStream('context:changed', {
      scope,
      include_metadata: true,
    });

    const handler = (data) => {
      if (data.scope === scope) {
        onChange(data);
      }
    };

    MCP_API.wsClient.on(`context:${scope}:changed`, handler);

    return {
      subscriptionId,
      unsubscribe: async () => {
        await MCP_API.unsubscribeFromEventStream(subscriptionId);
        MCP_API.wsClient.off(`context:${scope}:changed`, handler);
      },
    };
  },

  /**
   * Stream flow execution updates
   * @param {string} flowId - Flow ID to monitor
   * @param {function} onUpdate - Update handler
   */
  streamFlowExecution: async (flowId, onUpdate) => {
    const subscriptionId = await MCP_API.subscribeToEventStream('flow:execution', {
      flow_id: flowId,
      include_progress: true,
    });

    const handler = (data) => {
      if (data.flow_id === flowId) {
        onUpdate(data);
      }
    };

    MCP_API.wsClient.on(`flow:${flowId}:execution`, handler);

    return {
      subscriptionId,
      unsubscribe: async () => {
        await MCP_API.unsubscribeFromEventStream(subscriptionId);
        MCP_API.wsClient.off(`flow:${flowId}:execution`, handler);
      },
    };
  },

  /**
   * Stream MCP tool usage
   * @param {string} toolName - Tool name to monitor
   * @param {function} onUsage - Usage handler
   */
  streamToolUsage: async (toolName, onUsage) => {
    const subscriptionId = await MCP_API.subscribeToEventStream('tool:usage', {
      tool_name: toolName,
      include_metrics: true,
    });

    const handler = (data) => {
      if (data.tool_name === toolName) {
        onUsage(data);
      }
    };

    MCP_API.wsClient.on(`tool:${toolName}:usage`, handler);

    return {
      subscriptionId,
      unsubscribe: async () => {
        await MCP_API.unsubscribeFromEventStream(subscriptionId);
        MCP_API.wsClient.off(`tool:${toolName}:usage`, handler);
      },
    };
  },

  /**
   * Wait for connection with timeout
   */
  waitForConnection: async (timeout = 30000) => {
    if (MCP_API.wsClient.isConnected()) {
      return true;
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        MCP_API.wsClient.off('connected', onConnected);
        MCP_API.wsClient.off('connection_failed', onFailed);
        reject(new Error('WebSocket connection timeout'));
      }, timeout);

      const onConnected = () => {
        clearTimeout(timeoutId);
        resolve(true);
      };

      const onFailed = (error) => {
        clearTimeout(timeoutId);
        reject(error);
      };

      MCP_API.wsClient.once('connected', onConnected);
      MCP_API.wsClient.once('connection_failed', onFailed);

      // Start connection if not already connecting
      if (!MCP_API.wsClient.isConnecting()) {
        MCP_API.wsClient.connect().catch(reject);
      }
    });
  },

  // Enhanced tool execution with real-time updates
  executeToolRealtime: async (toolData, onProgress = null, onComplete = null) => {
    // Subscribe to progress updates
    if (onProgress) {
      MCP_API.subscribeToEvents('tool_progress', (data) => {
        if (data.tool_name === toolData.tool_name) {
          onProgress(data);
        }
      });
    }

    // Subscribe to completion events
    if (onComplete) {
      MCP_API.subscribeToEvents('tool_completed', (data) => {
        if (data.tool_name === toolData.tool_name) {
          onComplete(data);
          // Auto-unsubscribe after completion
          MCP_API.unsubscribeFromEvents('tool_completed', onComplete);
          if (onProgress) {
            MCP_API.unsubscribeFromEvents('tool_progress', onProgress);
          }
        }
      });
    }

    // Execute the tool
    return await MCP_API.executeTool(toolData);
  },

  // Real-time flow execution monitoring
  monitorFlowExecution: async (executionId, onUpdate = null) => {
    if (onUpdate) {
      MCP_API.subscribeToEvents('flow_status_update', (data) => {
        if (data.execution_id === executionId) {
          onUpdate(data);
        }
      });
    }

    // Initial status
    return await MCP_API.getFlowStatus(executionId);
  },

  // Enhanced server status with WebSocket info
  getEnhancedServerStatus: async () => {
    const httpStatus = await MCP_API.getServerStatus();
    const wsConnected = MCP_API.isWebSocketConnected();

    return {
      ...httpStatus,
      websocket: {
        connected: wsConnected,
        reconnectAttempts: MCP_API.wsClient.reconnectAttempts,
        maxReconnectAttempts: MCP_API.wsClient.maxReconnectAttempts,
      },
    };
  },

  // Private HTTP methods that use raw pythonClient (no timeout handling)
  _get: async (url, config = {}) => await makeRawRequest('GET', url, config),

  _post: async (url, data = {}, config = {}) => await makeRawRequest('POST', url, {
    ...config,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
    body: JSON.stringify(data),
  }),

  _put: async (url, data = {}, config = {}) => await makeRawRequest('PUT', url, {
    ...config,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
    body: JSON.stringify(data),
  }),

  _delete: async (url, config = {}) => await makeRawRequest('DELETE', url, config),

  // Enhanced methods with timeout handling
  getServerStatusWithTimeout: async (timeout = 5000) => {
    const cts = await getConnectionTimeoutService();
    if (cts) {
      return await cts.executeWithTimeout(
        '/api/v1/mcp/status',
        { method: 'GET' },
        { timeout },
      );
    }
    // Fallback to direct request
    return await makeRawRequest('GET', '/api/v1/mcp/status');
  },

  executeToolWithTimeout: async (toolData, timeout = 30000) => {
    const cts = await getConnectionTimeoutService();
    if (cts) {
      return await cts.executeWithTimeout(
        '/mcp/execute',
        { method: 'POST', body: toolData },
        { timeout },
      );
    }
    // Fallback to direct request
    return await makeRawRequest('POST', '/mcp/execute', { body: toolData });
  },

  // Service availability checking
  checkAvailability: async (timeout = 3000) => {
    const cts = await getConnectionTimeoutService();
    if (cts) {
      return await cts.checkServiceAvailability('all', timeout);
    }
    return { available: true, services: [] };
  },

  // Connection state management
  getConnectionMetrics: async () => {
    const cts = await getConnectionTimeoutService();
    if (cts) {
      return cts.getMetrics();
    }
    return {
      requestTimeouts: 0,
      websocketTimeouts: 0,
      serviceTimeouts: 0,
      retryCount: 0,
      successCount: 0,
    };
  },

  getConnectionStates: async () => {
    const cts = await getConnectionTimeoutService();
    if (cts) {
      return cts.getConnectionStates();
    }
    return new Map();
  },

  // Enhanced WebSocket request with timeout
  sendWebSocketRequest: async (method, params = {}, timeout = 30000) => {
    const cts = await getConnectionTimeoutService();
    if (cts) {
      return await cts.executeWebSocketRequest(method, params, timeout);
    }
    throw new Error('WebSocket not available - connectionTimeoutService not loaded');
  },
};

export default MCP_API;
